import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { AccountListItem } from '@bliks-io/contracts';
import {
  Button,
  DataTable,
  Page,
  PageContent,
  createActionsColumn,
  createColumnHeader,
  Alert,
  AlertDescription,
  cn,
  type DataTableColumnDef
} from '@bliks-io/ui';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import { useListAccounts } from '@/pages/admin/api/queries/useAccounts';
import { useAddAccount } from '@/pages/admin/api/mutations/useAccounts';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AddAccountDialog } from './components/AddAccountDialog';

/**
 * The customers, one row each — the level above tenants (LIC-06). A customer
 * is one legal entity and may run several workspaces: a consultancy runs a
 * partner one and its own.
 */
export function AccountListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const { data, isLoading } = useListAccounts();
  const addAccount = useAddAccount();
  useDocumentTitle(t('accountAdmin.title'));

  /** Derselbe Weg wie der Eintrag im Zeilenmenü — der Name ist nur der kürzere. */
  const open = useCallback(
    (accountId: string) =>
      void navigate({
        to: '/admin/accounts/$accountId',
        params: { accountId }
      }),
    [navigate]
  );

  const create = (hubspotCompanyId?: string) => {
    addAccount.mutate(
      { ...(hubspotCompanyId && { hubspotCompanyId }) },
      {
        onSuccess: ({ account }) => {
          setAddOpen(false);
          void navigate({
            to: '/admin/accounts/$accountId',
            params: { accountId: account._id }
          });
        },
        onError: (error) => toast.error(translateErrorMessage(error as unknown as ApiError, t))
      }
    );
  };

  const columns = useMemo<DataTableColumnDef<AccountListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: createColumnHeader(t('accountAdmin.columnName')),
        cell: ({ row }) => {
          const { _id, name, domain, hubspotCompanyId } = row.original;
          return (
            <div className="flex flex-col">
              {/* A nameless row is not an empty one: it still identifies the
                  customer by the company id we hold. Anklickbar wie in der
                  Mandantenliste: der Name ist das, worauf man zeigt. */}
              <span
                className={cn(
                  'cursor-pointer hover:text-primary hover:underline',
                  name ? 'font-medium' : 'font-medium text-muted-foreground'
                )}
                onClick={() => open(_id)}
              >
                {name ?? t('accountAdmin.unnamed')}
              </span>
              <span className="text-xs text-muted-foreground">
                {domain ?? hubspotCompanyId ?? t('accountAdmin.noCompany')}
              </span>
            </div>
          );
        }
      },
      {
        accessorKey: 'tenantCount',
        header: createColumnHeader(t('accountAdmin.columnTenants')),
        cell: ({ row }) => row.original.tenantCount
      },
      {
        accessorKey: 'parent',
        header: createColumnHeader(t('accountAdmin.columnGroup')),
        cell: ({ row }) => {
          const { parent, childCount } = row.original;
          // Both halves of belonging: whose child this is, and how many it has.
          if (parent) return parent.name ?? t('accountAdmin.unnamed');
          if (childCount > 0) {
            return (
              <span className="text-muted-foreground">
                {t('accountAdmin.childCount', { count: childCount })}
              </span>
            );
          }
          return <span className="text-muted-foreground">—</span>;
        }
      },
      createActionsColumn<AccountListItem>({
        onView: (account) => open(account._id)
      })
    ],
    [open, t]
  );

  return (
    <Page>
      <PageContent>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('accountAdmin.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('accountAdmin.description')}</p>
          </div>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('accountAdmin.addAccount')}
          </Button>
        </div>

        {/* The list is our own data; only the names come from HubSpot. Saying so
            keeps "we could not ask" apart from "this customer has no company". */}
        {data && !data.companiesResolved && (
          <Alert colorScheme="warning" className="mb-4">
            <AlertDescription>{t('accountAdmin.companiesUnavailable')}</AlertDescription>
          </Alert>
        )}

        <DataTable
          columns={columns}
          data={data?.accounts ?? []}
          isLoading={isLoading}
          emptyMessage={t('accountAdmin.empty')}
        />

        <AddAccountDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          onCreate={create}
          isCreating={addAccount.isPending}
        />
      </PageContent>
    </Page>
  );
}
